import { Flex, FlexItem } from "orcs-design-system";
import ErrorNotification from "src/components/ErrorNotification";
import CreateNewTagModal from "./sub-components/CreateNewTagModal/CreateNewTagModal";
import ExpandableBadgeList from "./sub-components/ExpandableBadgeList";
import { Line } from "./sub-components/PlaceholderStyles";
import TagAttributesModal from "./sub-components/TagAttributesModal";
import TagDisplayValueModal from "./sub-components/TagDisplayValueModal";
import TagEditBtn from "./sub-components/TagEditBtn";
import TagTypeHelpModal from "./sub-components/TagTypeHelpModal";
import {
  showTagsEditorEditButton,
  showTagsEditorReadBadges,
} from "./TagsEditor.views.slots";
import type { TagsEditorViewModel } from "./tagsEditorTypes";

type ViewProps = { vm: TagsEditorViewModel };

export const TagsEditorInlineView = ({ vm }: ViewProps) => (
  <>
    <ExpandableBadgeList
      tags={vm.existingTagsViewModel as never}
      onEntityTagClick={vm.onEntityTagClick}
      withAppliedCount={vm.withAppliedCount}
      small={vm.small}
      highlighted={vm.highlighted}
      inline={vm.inline}
      onClickTagInfo={vm.onEditTagAttributes}
      entityType={vm.entityType}
    />
    {vm.attributesModalProps && (
      <TagAttributesModal
        {...vm.attributesModalProps}
        onClose={vm.onCloseTagAttributes}
        entityId={vm.entityId}
        tagTypes={vm.tagTypes as never}
        tagsQueryTagTypes={vm.tagsQueryTagTypes as never}
        featureFlags={vm.featureFlags as never}
        editMode={vm.editMode || vm.editOnly}
        readOnly
        entityType={vm.entityType}
        onTagUpdated={vm.onTagUpdated}
      />
    )}
  </>
);

const TagsEditorSelectorSlot = ({ vm }: ViewProps) => (
  <>
    {vm.helpTagType && (
      <TagTypeHelpModal tagType={vm.helpTagType} onClose={vm.hideHelp} />
    )}
    {vm.loading && !vm.editMode && (
      <Line width={200} height={24} role="alert" aria-busy={true} />
    )}
    {(vm.editMode || vm.editOnly) && (!vm.loading || vm.editMode) && (
      <vm.TagSelector
        selectorRef={vm.tagSelectorRef}
        loading={vm.loading}
        existingTags={vm.existingTagsViewModel}
        onError={vm.setError}
        setNewTag={vm.onAddNewTag}
        removeTag={vm.onRemoveTag}
        onTagsLoaded={vm.onTagsLoaded}
        onBlur={vm.finishEditing}
        onEntityTagClick={vm.onEntityTagClick}
        tagTypesConfig={vm.tagTypesConfigViewModel}
        tagTypes={vm.tagTypes}
        showTagTypeHelp={vm.showTagTypeHelp}
        helpIsOpen={!!vm.helpTagType}
        isAssociationTag={vm.isAssociationTag}
        onEditTagAttributes={vm.onEditTagAttributes}
        onCreateNewTag={vm.onCreateNewTag}
        label={vm.label}
        bulkTagMode={vm.bulkTagMode}
        entityType={vm.entityType}
        menuPlacement={vm.menuPlacement}
        menuPortalTarget={vm.menuPortalTarget}
        hideDropdown={vm.hideDropdown}
      />
    )}
  </>
);

const TagsEditorReadSlot = ({ vm }: ViewProps) => (
  <>
    {showTagsEditorReadBadges(vm) && (
      <ExpandableBadgeList
        tags={vm.existingTagsViewModel as never}
        onEntityTagClick={vm.onEntityTagClick}
        withAppliedCount={vm.withAppliedCount}
        small={vm.small}
        onClickTagInfo={vm.onEditTagAttributes}
        entityType={vm.entityType}
      />
    )}
    {showTagsEditorEditButton(vm) && (
      <FlexItem flex="0 0 auto" mt={vm.addMargin}>
        <TagEditBtn
          onEditClicked={vm.startEditing}
          onFinishEditClicked={vm.finishEditing}
          isTagsEmpty={vm.isTagsEmpty}
          icon={vm.editButtonIcon || vm.icons["pen"]}
          {...(vm.editMode !== undefined ? { editMode: vm.editMode } : {})}
          {...(vm.isAssociationTag !== undefined
            ? { isAssociationTag: vm.isAssociationTag }
            : {})}
          {...(vm.editButtonLabel !== undefined
            ? { editButtonLabel: vm.editButtonLabel }
            : {})}
        />
      </FlexItem>
    )}
    {vm.error && (
      <ErrorNotification message={vm.error?.message} error={vm.error} />
    )}
  </>
);

const TagsEditorModals = ({ vm }: ViewProps) => (
  <>
    {vm.attributesModalProps && (
      <TagAttributesModal
        {...vm.attributesModalProps}
        onClose={vm.onCloseTagAttributes}
        entityId={vm.entityId}
        tagTypes={vm.tagTypes as never}
        tagsQueryTagTypes={vm.tagsQueryTagTypes as never}
        featureFlags={vm.featureFlags as never}
        tagTypesConfig={vm.tagTypesConfig as never}
        editMode={vm.editMode || vm.editOnly}
        readOnly={vm.readOnly}
        entityType={vm.entityType}
        onTagUpdated={vm.onTagUpdated}
      />
    )}
    {vm.tagValueModalProps && (
      <TagDisplayValueModal
        {...vm.tagValueModalProps}
        onClose={vm.onCloseTagValueModal}
      />
    )}
    {vm.newTagCreationModalProps && (
      <CreateNewTagModal
        {...vm.newTagCreationModalProps}
        onClose={vm.onCloseNewTagCreationModal}
        featureFlags={vm.featureFlags as never}
        tagTypesConfig={vm.tagTypesConfig as never}
      />
    )}
  </>
);

export const TagsEditorChrome = ({ vm }: ViewProps) => (
  <FlexItem flex={vm.flexStack} mt={vm.mt || vm.addMargin}>
    <Flex
      alignItems="flex-start"
      justifyContent="space-between"
      flexDirection={vm.noContent ? "row" : "column"}
    >
      <TagsEditorSelectorSlot vm={vm} />
      <TagsEditorReadSlot vm={vm} />
    </Flex>
    <TagsEditorModals vm={vm} />
  </FlexItem>
);
